import _ from 'lodash';
import moment, { Moment } from 'moment';
import 'moment/locale/ro';
import React, { useContext } from 'react';
import {
  checkBoundedDayType,
  createInitialAttendanceState,
  formatWeekToStandard,
  isWeekend,
} from '.';
import useBreakpoints from '../../../../../../common/hooks/useBreakpoints';
import Icons from '../../../../../../common/widgets/Icons';
import { config } from '../../../../config';
import { CustomerInfoContext } from '../../../../contexts/CustomerInfoContext';
import { TimesheetPresenceTypeCategories } from '../../../../enums';
import {
  IndexedArray,
  IndexedTimesheet,
  PresenceTypes,
  Timesheet,
  TimesheetBankHolidays,
} from '../../../../models/TimeAndAttendance';
import { MobileTimeAndAttendanceCalendar } from './MobileTimeAndAttendanceCalendar';

interface ITimeAndAttendanceCalendarProps {
  currentDate: Moment;
  timesheetData: Timesheet[] | undefined;
  timesheetBankHolidaysData: TimesheetBankHolidays[];
  bankHolidayPresenceType: PresenceTypes | undefined;
  isTimesheetBlocked: boolean;
  setSelectedAttendance: React.Dispatch<
    React.SetStateAction<Timesheet | undefined>
  >;
  setShowAttendanceDialog: React.Dispatch<React.SetStateAction<boolean>>;
}

export const TimeAndAttendanceCalendar = (
  props: ITimeAndAttendanceCalendarProps,
) => {
  const { isTablet } = useBreakpoints();
  const { customerInfo } = useContext(CustomerInfoContext);

  const selectedLanguage =
    customerInfo?.languageCode ?? config.defaultLanguage ?? 'ro';
  const weekDays =
    selectedLanguage === 'en'
      ? formatWeekToStandard(moment.weekdays(true))
      : moment.weekdays(true);

  let firstDayOfMonthIndex =
    moment(new Date(props.currentDate.toDate())).startOf('month').day() || 7;
  const daysInCurrentMonth = moment(props.currentDate).daysInMonth();

  const calendarDays: IndexedArray[] = [
    ...Array.from({ length: firstDayOfMonthIndex - 1 }, (_, index) => ({
      index: 1 + index - firstDayOfMonthIndex,
    })),
    ...Array.from({ length: daysInCurrentMonth }, (_, index) => ({
      index: index + 1,
    })),
  ];

  if (firstDayOfMonthIndex > 0) {
    --firstDayOfMonthIndex;
  }

  props.timesheetBankHolidaysData
    .filter(
      holiday =>
        new Date(holiday.bankHoliday).getMonth() == props.currentDate.month() &&
        new Date(holiday.bankHoliday).getFullYear() == props.currentDate.year(),
    )
    .forEach((holiday: TimesheetBankHolidays) => {
      if (
        !props.timesheetData?.some(
          t =>
            new Date(t.startDate).getDate() ==
            new Date(holiday.bankHoliday).getDate(),
        )
      ) {
        props.timesheetData?.push({
          startDate: new Date(holiday.bankHoliday),
          endDate: new Date(holiday.bankHoliday),
          presenceType: {
            presenceTypeId: props.bankHolidayPresenceType?.presenceTypeId,
            presenceTypeDescription:
              props.bankHolidayPresenceType?.presenceTypeDescription ??
              'Sarbatoare legala',
            categoryId: TimesheetPresenceTypeCategories.Other,
            highlightColor:
              props.bankHolidayPresenceType?.highlightColor ?? 'e8e8e8',
            textColor: props.bankHolidayPresenceType?.textColor ?? '4c4a4a',
          },
          id: '',
          submitted: false,
          workEvent: {
            startWorkHour: '00:00:00',
            endWorkHour: '00:00:00',
            startBreakHour: '00:00:00',
            endBreakHour: '00:00:00',
          },
          files: [],
        });
      }
    });

  props.timesheetData?.forEach((timesheet: Timesheet) => {
    let startDay = moment(timesheet.startDate).date();
    let endDay = moment(timesheet.endDate).date();
    let isBounded = startDay < endDay;

    while (startDay <= endDay) {
      calendarDays[firstDayOfMonthIndex - 1 + startDay] = {
        ...calendarDays[firstDayOfMonthIndex - 1 + startDay],
        ...timesheet,
        isBounded,
      };
      startDay += 1;
    }
  });

  let formatedCalendarDaysByWeek = [];
  let startIndex = 0;
  while (startIndex < calendarDays.length) {
    let slicedCalendarDaysByWeek = calendarDays.slice(
      startIndex,
      startIndex + 7,
    );
    if (isTablet) {
      formatedCalendarDaysByWeek.push(
        ...slicedCalendarDaysByWeek.map((calendarDay, index) => ({
          ...calendarDay,
          weekDay: weekDays[index % 7].at(0)?.toUpperCase(),
        })),
      );
    } else {
      formatedCalendarDaysByWeek.push(slicedCalendarDaysByWeek);
    }
    startIndex += 7;
  }

  const handleSelectEmptyAttendace = (
    currentDate: Moment,
    calendarDay: IndexedTimesheet,
  ) => {
    props.setSelectedAttendance({
      ...createInitialAttendanceState(null),
      startDate: (() => {
        const startDate = currentDate.toDate();
        startDate.setDate(calendarDay.index);
        return startDate;
      })(),
      endDate: (() => {
        const endDate = currentDate.toDate();
        endDate.setDate(calendarDay.index);
        return endDate;
      })(),
    });
    props.setShowAttendanceDialog(true);
  };

  return (
    <div
      className={
        isTablet ? 'mobile-taa-calendar-container' : 'taa-calendar-container'
      }
    >
      {!isTablet ? (
        <table>
          <thead>
            <tr className="weekday-header-container">
              {_.map(weekDays, (weekDay, index: number) => (
                <th key={index} className="weekday-header-cell">
                  {weekDay}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="taa-calendar-container-body">
            {_.map(
              formatedCalendarDaysByWeek,
              (calendarWeek: IndexedTimesheet[], index: number) => (
                <tr key={index} className="taa-calendar-week">
                  {_.map(
                    calendarWeek,
                    (calendarDay: IndexedTimesheet, index: number) => (
                      <td
                        key={index}
                        className={`taa-calendar-day ${
                          isWeekend(calendarDay.index, firstDayOfMonthIndex)
                            ? 'inactive'
                            : 'active'
                        } ${
                          calendarDay.isBounded
                            ? checkBoundedDayType(
                                calendarWeek[index - 1],
                                calendarDay,
                                calendarWeek[index + 1],
                              )
                            : ''
                        }`}
                      >
                        <div className={`taa-calendar-day-content`}>
                          {calendarDay && calendarDay.index > 0 && (
                            <>
                              <div>{calendarDay.index}</div>
                              {calendarDay?.presenceType ? (
                                <button
                                  type="button"
                                  disabled={props.isTimesheetBlocked}
                                  onClick={() => {
                                    props.setSelectedAttendance({
                                      ...calendarDay,
                                      startDate: new Date(
                                        calendarDay.startDate,
                                      ),
                                      endDate: new Date(calendarDay.endDate),
                                      workEvent: {
                                        ...createInitialAttendanceState(null)
                                          .workEvent,
                                        ...calendarDay.workEvent,
                                      },
                                      files: calendarDay.files ?? [],
                                    });
                                    props.setShowAttendanceDialog(true);
                                  }}
                                  className="taa-calendar-day-event"
                                  style={{
                                    backgroundColor: `#${calendarDay?.presenceType.highlightColor}`,
                                    color: `#${calendarDay?.presenceType.textColor}`,
                                  }}
                                >
                                  {
                                    calendarDay.presenceType
                                      .presenceTypeDescription
                                  }
                                </button>
                              ) : (
                                <>
                                  {props.isTimesheetBlocked ? (
                                    <></>
                                  ) : (
                                    <button
                                      type="button"
                                      className="taa-calendar-day-empty"
                                      onClick={() => {
                                        handleSelectEmptyAttendace(
                                          props.currentDate,
                                          calendarDay,
                                        );
                                      }}
                                    >
                                      <Icons.ProductAdd />
                                    </button>
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    ),
                  )}
                </tr>
              ),
            )}
          </tbody>
        </table>
      ) : (
        <MobileTimeAndAttendanceCalendar
          currentDate={props.currentDate}
          calendarDays={formatedCalendarDaysByWeek}
          isTimesheetBlocked={props.isTimesheetBlocked}
          setShowAttendanceDialog={props.setShowAttendanceDialog}
          setSelectedAttendance={props.setSelectedAttendance}
          firstDayOfMonthIndex={firstDayOfMonthIndex}
          handleSelectEmptyAttendace={handleSelectEmptyAttendace}
        />
      )}
    </div>
  );
};
